// AML-Sumsub-1 (2026-07-07) — reusable Sumsub WebSDK loader.
//
// This is the shared launcher for individual KYC (directors, guarantors,
// beneficial owners). It is intentionally NOT wired into the apply flow yet —
// that is the next PR. It fetches a short-lived access token from our backend
// (/api/aml/sumsub/access-token), lazy-loads the Sumsub WebSDK from their CDN,
// and launches the verification UI into a container element.
//
// Usage (next PR):
//   const info = await fetchSumsubToken({ beneficialOwnerId });
//   await launchSumsub({ containerId: "sumsub-mount", tokenInfo: info,
//                        onComplete: (p) => ..., onError: (e) => ... });

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SDK_SRC = "https://static.sumsub.com/idensic/static/sns-websdk-builder.js";

let _sdkPromise = null;

function loadSdk() {
  if (typeof window !== "undefined" && window.snsWebSdk) {
    return Promise.resolve(window.snsWebSdk);
  }
  if (_sdkPromise) return _sdkPromise;
  _sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SDK_SRC;
    script.async = true;
    script.onload = () => resolve(window.snsWebSdk);
    script.onerror = () => reject(new Error("Failed to load Sumsub WebSDK"));
    document.head.appendChild(script);
  });
  return _sdkPromise;
}

// Ask our backend to create/reuse the Sumsub applicant for a person and return
// a fresh access token. Pass either a beneficialOwnerId (preferred) or an
// explicit externalUserId.
export async function fetchSumsubToken({ beneficialOwnerId, externalUserId, email } = {}) {
  const res = await axios.post(`${API}/aml/sumsub/access-token`, {
    beneficial_owner_id: beneficialOwnerId,
    external_user_id: externalUserId,
    email,
  });
  return res.data; // { token, applicant_id, level_name, external_user_id }
}

// Launch the WebSDK into the element with id `containerId`.
export async function launchSumsub({ containerId, tokenInfo, onComplete, onError } = {}) {
  if (!tokenInfo || !tokenInfo.token) {
    throw new Error("launchSumsub requires a tokenInfo with a token");
  }
  const snsWebSdk = await loadSdk();

  // The SDK calls this when the token expires mid-flow — mint a new one.
  const refreshToken = async () => {
    const info = await fetchSumsubToken({ externalUserId: tokenInfo.external_user_id });
    return info.token;
  };

  const sdk = snsWebSdk
    .init(tokenInfo.token, refreshToken)
    .withConf({ lang: "en" })
    .on("idCheck.onApplicantStatusChanged", (payload) => {
      if (typeof onComplete === "function") onComplete(payload);
    })
    .on("idCheck.onError", (error) => {
      if (typeof onError === "function") onError(error);
    })
    .build();

  sdk.launch(`#${containerId}`);
  return sdk;
}
